import { produce } from 'immer';
import type { RecordOf } from 'immutable';
import Immutable from 'immutable';
import { DateTime, Interval } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import { mkFilterObject } from 'app-components/flux/filters/filtersTypes';
import type { Filter, FilterObjectProps, UsageFilterItemProps } from 'app-components/flux/filters/filtersTypes';
import {
  convertFilterObjectToQCSFilter,
  convertQCSFilterToFilterObject,
} from 'app-components/flux/filters/filtersUtils';
import type {
  DateRangeType,
  DensityType,
  PersistentType,
  ReportConfiguration,
  ReportNameType,
  ReportSubType,
  ReportType,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { mkReportConfiguration } from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { getLogger } from 'app-components/utils/core';
import {
  constructPredefinedInterval,
  convertGeneratedReportDateRangeToPredefinedRange,
  getStartOfToday,
} from 'app-components/utils/date';

import { assert } from 'utils/assert';
import type { ReportsState } from './types';

////////////////////////////////////////////////////////////////////////////////////////////////////
// Context State -> ReportConfiguration
////////////////////////////////////////////////////////////////////////////////////////////////////
export const transformStateToReportConfiguration = (state: ReportsState): ReportConfiguration => {
  const newState = produce(state, (draft) => {
    draft.id = state.id;
    draft.clientId = state.clientId || uuidv4().toUpperCase();
    draft.name = state.name;
    draft.tabName = state.name as ReportNameType;
    draft.defaultBusiness = state.defaultBusiness;
    draft.today = getStartOfToday(DateTime.local());
    draft.displayOptions = state.displayOptions;

    draft.loading = state.loading || false;
    draft.accountingType = state.accountingType;
    draft.chartType = state.chartType;

    draft.reportType = state.reportType;
    draft.reportSubType = state.reportSubType;
    draft.groupBy = state.groupBy;
    draft.segmentBy = state.segmentBy;
    draft.timestep = state.timestep;

    draft.breadcrumbs = state.breadcrumbs || [];
    draft.filterId = state.filterId;
    draft.dateRange = {
      id: state.dateRange?.id || 'CUSTOM',
      interval: state.dateRange?.interval || constructPredefinedInterval({ value: 'LAST_6_MONTHS' }, DateTime.local()),
    } as DateRangeType;
  });

  // we add the state.usage array to the filter on the way
  const combineUsage = ((newState.filter as RecordOf<FilterObjectProps>) || mkFilterObject()).set(
    'businesses',
    state.usage ? Immutable.Set(state.usage) : undefined,
  );

  const configuration = mkReportConfiguration({
    name: newState.name as ReportNameType,
    accountingType: newState.accountingType,
    reportType: newState.reportType,
    reportSubType: newState.reportSubType as ReportSubType,
    persistentType: 'TEMPORARY' as PersistentType,
    dateRange: newState.dateRange.id,
    ...(newState.dateRange.id === 'CUSTOM' ? { startOn: newState.dateRange.interval.start?.toISODate() } : {}),
    ...(newState.dateRange.id === 'CUSTOM' ? { endOn: newState.dateRange.interval.end?.toISODate() } : {}),
    rowType: newState.groupBy,
    columnType: newState.segmentBy,
    timestep: newState.timestep,
    // timestep: newState.timestep,
    filterId: newState.filterId,
    filter: convertFilterObjectToQCSFilter(mkFilterObject(combineUsage)),
    id: newState.id,
    clientId: newState.clientId,
    // options: newState.options,
    displayOptions: {
      ...newState.displayOptions,
      density: (newState.displayOptions?.density?.toUpperCase() as DensityType) || 'COMFORTABLE',
    },
    chartType: newState.chartType,
  });
  return configuration;
};

////////////////////////////////////////////////////////////////////////////////////////////////////
// ReportConfiguration -> Context State
////////////////////////////////////////////////////////////////////////////////////////////////////

const getTabName = (reportType: ReportType, reportSubType: ReportSubType, businessEnabled: boolean): ReportNameType => {
  if (reportType === 'SUMMARY' && reportSubType === 'SPENDING') {
    return 'total-spending';
  }
  if (reportType === 'SUMMARY' && reportSubType === 'INCOME') {
    return 'income';
  }
  if (businessEnabled && reportType === 'SUMMARY' && reportSubType === 'NET_INCOME') {
    return 'profit-loss';
  }
  if (reportType === 'SUMMARY' && reportSubType === 'NET_INCOME') {
    return 'net-income';
  }
  if (reportType === 'SUMMARY' && reportSubType === 'SAVINGS') {
    return 'savings';
  }
  if (reportType === 'SUMMARY' && reportSubType === 'NET_WORTH') {
    return 'net-worth';
  }
  if (
    (reportType === 'SUMMARY' && reportSubType === 'TAX_SUMMARY') ||
    (reportType === 'TRANSACTION' && reportSubType === 'TAX_SUMMARY')
  ) {
    return 'taxes';
  }
  if (reportType === 'SUMMARY' && reportSubType === 'BALANCE_SHEET') {
    return 'balance-sheet';
  }
  assert(false, `Unknown report type: ${reportType}, sub type: ${reportSubType}`);
  return 'custom'; // Fallback
};

const getIntervalFromReportConfiguration = (reportConfiguration: ReportConfiguration): Interval => {
  if (typeof reportConfiguration.dateRange === 'string' && reportConfiguration.dateRange !== 'CUSTOM') {
    const predefinedDateRange = convertGeneratedReportDateRangeToPredefinedRange(reportConfiguration.dateRange);
    if (!predefinedDateRange) {
      assert(false, `Unknown date range: ${reportConfiguration.dateRange}`);
      return Interval.invalid('Unknown date range');
    }
    return constructPredefinedInterval({ value: predefinedDateRange }, DateTime.local());
  } else {
    return Interval.fromDateTimes(
      DateTime.fromISO(reportConfiguration.startOn ?? '1900-01-01'),
      DateTime.fromISO(reportConfiguration.endOn ?? '2100-01-01'),
    );
  }
};

// convert any USAGE filters into state usage array
const getUsageFromReportConfiguration = (
  filter: Filter,
  businessEnabled: boolean,
  openBusinesses: string[],
): string[] => {
  const logger = getLogger('getUsageFromReportConfiguration');
  const usageIndex = filter?.filterItems?.findIndex((item) => item.type === 'USAGE');

  let usage: string[] = [];
  if (typeof usageIndex === 'number' && usageIndex >= 0) {
    usage = ((filter?.filterItems?.[usageIndex] as UsageFilterItemProps).usages.map((item) =>
      item.id === '0' ? 'PERSONAL' : item.id,
    ) ?? []) as string[];
    logger.info('Usage extracted from ReportConfiguration:', usage);
  } else {
    usage = businessEnabled ? openBusinesses : ['PERSONAL'];
    logger.info('No USAGE in ReportConfiguration - defaulting usage to:', usage);
  }

  return usage;
};

export const transformReportConfigurationToState = (
  reportConfiguration: ReportConfiguration,
  businessEnabled: boolean = true,
  openBusinessesIds: string[] = [],
): ReportsState => {
  assert(reportConfiguration, 'Report configuration is required');
  const logger = getLogger('transformReportConfigurationToState');

  const interval = getIntervalFromReportConfiguration(reportConfiguration);
  const dateRange = {
    id: typeof reportConfiguration.dateRange === 'string' ? reportConfiguration.dateRange : 'CUSTOM',
    interval: interval,
  } as DateRangeType;
  // set the usage from the filter on they way in
  const usage = reportConfiguration.filter
    ? getUsageFromReportConfiguration(reportConfiguration.filter, businessEnabled, openBusinessesIds)
    : [];
  const filter = reportConfiguration.filter
    ? convertQCSFilterToFilterObject(reportConfiguration.filter)
    : mkFilterObject();

  const newState = {
    // directly mapped fields
    id: reportConfiguration.id,
    clientId: reportConfiguration.clientId,
    name: reportConfiguration.name,
    accountingType: reportConfiguration.accountingType,
    reportType: reportConfiguration.reportType,
    reportSubType: reportConfiguration.reportSubType,
    groupBy: reportConfiguration.rowType,
    segmentBy: reportConfiguration.columnType,
    timestep: reportConfiguration.timestep,
    displayOptions: {
      ...reportConfiguration.displayOptions,
      density: reportConfiguration.displayOptions?.density ?? 'STANDARD',
      showPLGraph: reportConfiguration.displayOptions?.showPLGraph ?? true,
      alternateRows: reportConfiguration.displayOptions?.alternateRows ?? false,
      hideCents: reportConfiguration.displayOptions?.hideCents ?? false,
      showExpensesAsNegative: reportConfiguration.displayOptions?.showExpensesAsNegative ?? false,
      showOtherOnTop: reportConfiguration.displayOptions?.showOtherOnTop ?? false,
      showSubTotalsOnTop: reportConfiguration.displayOptions?.showSubTotalsOnTop ?? false,
    },

    // calculated fields
    tabName:
      (reportConfiguration.name as ReportNameType) ||
      getTabName(reportConfiguration.reportType, reportConfiguration.reportSubType, businessEnabled),
    today: getStartOfToday(DateTime.local()),
    dateRange: dateRange,
    filterId: reportConfiguration.filterId,
    filter: filter,
    usage: usage,
    chartType: reportConfiguration.chartType,
    // options: reportConfiguration.options,
    breadcrumbs: [],
    loading: false,
  };
  logger.log('State from ReportConfiguration:', newState);
  return newState;
};
